/* global Shopify */

import React, {useMemo, useState} from "react";

import PropTypes from "prop-types";
import {userManager} from "../../managers/UserManager";
import {Picture} from "../../components/Picture";
import {SelfHostedServerContextButton} from "./SelfHostedServerContextButton";
import {SelfHostedServerDeleteDialog} from "./SelfHostedServerDeleteDialog";
import {SelfHostedServerActionButton} from "./SelfHostedServerActionButton";
import {SelfHostedServerEditCustomDomainDialog} from "./SelfHostedServerEditCustomDomainDialog";
import {SelfHostedServerTransferLicenseDialog} from "./SelfHostedServerTransferLicenseDialog";
import {SelfHostedServerStatus} from "./SelfHostedServerStatus";
import {SelfHostedServerExpirationDate} from "./SelfHostedServerExpirationDate";
import {i18nFormat} from "../../lib/i18nFormat";
import {useMediaBreakpoint} from "../../hooks/useMediaBreakpoint";

export function SelfHostedServer(props) {
  const servers = userManager.getSelfHostedServers()
  const homeySHSImage = "/img/pages/account/self-hosted-servers/homey-self-hosted-server.png";
  const [isTransferLicenseDialogOpen, setIsTransferLicenseDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isEditCustomDomainDialogOpen, setIsEditCustomDomainDialogOpen] = useState(false);
  const {breakpointUp, breakpointDown} = useMediaBreakpoint()

  const serverActions = useMemo(() => {
    const actions = [];

    if (props.server.selfHostedServerSubscriptionType === "trial") {
      actions.push("start-subscription");
    }
    if (props.server.selfHostedServerSubscriptionType === "monthly") {
      if (props.server.selfHostedServerSubscriptionActive === true) {
        actions.push("manage-subscription");
      } else {
        actions.push("resume-subscription");
      }
    }
    if (props.server.selfHostedServerSubscriptionType === "lifetime") {
      // Allow transfer only if there is more than one server and at least one other server is not lifetime licensed
      if (servers.length > 1 && servers.some((server) => server.id !== props.server.id && server.selfHostedServerSubscriptionType !== "lifetime")) {
        actions.push("transfer-license");
      }
    } else {
      actions.push("buy-lifetime-license");
    }

    return actions;
  }, [servers, props.server]);

  function handleManageSubscriptionClick() {
    const shopify = new Shopify();
    let url = shopify.getAccountSubscriptionUrl();
    if (props.server.selfHostedServerSubscriptionShopifyContractId) {
      url = `${url}/subscriptions/${props.server.selfHostedServerSubscriptionShopifyContractId.replace('gid://shopify/SubscriptionContract/', '')}`;
    }

    window.open(url);
  }

  function handleStartSubscriptionClick() {
    Promise.resolve().then(async () => {
      const shopify = new Shopify();
      const merchandiseId = await shopify.getProductVariantIdByWebsiteReference("homey-shs-monthly");
      const sellingPlanId = await shopify.getSellingPlanIdByWebsiteReference("homey-shs-monthly");
      const cart = await shopify.createCart({
        lines: [{
          merchandiseId,
          sellingPlanId,
          quantity: 1,
          attributes: [{
            key: "Homey ID",
            value: props.server.id,
          }],
        }],
      });

      window.location.href = cart.checkoutUrl;
    }).catch(error => alert(error));
  }

  function handleBuyLifetimeLicenseClick() {
    Promise.resolve().then(async () => {
      const shopify = new Shopify();
      const merchandiseId = await shopify.getProductVariantIdByWebsiteReference("homey-shs-lifetime");
      const cart = await shopify.createCart({
        lines: [{
          merchandiseId,
          quantity: 1,
          attributes: [{
            key: "Homey ID",
            value: props.server.id,
          }],
        }],
      });

      window.location.href = cart.checkoutUrl;
    }).catch(error => alert(error));
  }

  function openTransferLicenseDialog() {
    setIsTransferLicenseDialogOpen(true);
  }

  function closeTransferLicenseDialog() {
    setIsTransferLicenseDialogOpen(false);
  }

  function openDeleteDialog() {
    setIsDeleteDialogOpen(true);
  }

  function closeDeleteDialog() {
    setIsDeleteDialogOpen(false);
  }

  function openEditCustomDomainDialog() {
    setIsEditCustomDomainDialogOpen(true);
  }

  function closeEditCustomDomainDialog() {
    setIsEditCustomDomainDialogOpen(false);
  }

  return (
    <div className={`account-shs ${serverActions.length > 0 ? "account-shs--with-actions" : ""}`}>
      <div className="account-shs__component">
        <Picture className="account-shs__image" src={homeySHSImage} alt="Homey Self-Hosted Server"/>
        <div className="account-shs__content">
          <div className="account-shs__info">
            <h3 className="account-shs__name">
              {props.server.name}
              {typeof props.server.selfHostedServerCustomHostname === "string" && (
                <span className="account-shs__custom-domain">
                  {props.server.selfHostedServerCustomHostname}
                </span>
              )}
            </h3>
            <div className="account-shs__details">
              <SelfHostedServerStatus server={props.server}></SelfHostedServerStatus>
              <SelfHostedServerExpirationDate server={props.server}></SelfHostedServerExpirationDate>
            </div>
            {breakpointUp("md") && (
              <SelfHostedServerContextButton
                onEditCustomDomain={openEditCustomDomainDialog}
                onDelete={openDeleteDialog}
              />
            )}
          </div>
          {serverActions.length > 0 && (
            <div className="account-shs__actions">
              {serverActions.includes("start-subscription") && (
                <SelfHostedServerActionButton
                  onClick={handleStartSubscriptionClick}
                  glyph={"glyph-core-rectangle-pen"}
                  text={i18nFormat("shs.server-actions.start-subscription")}
                />
              )}
              {serverActions.includes("manage-subscription") && (
                <SelfHostedServerActionButton
                  onClick={handleManageSubscriptionClick}
                  glyph={"glyph-core-rectangle-pen"}
                  text={i18nFormat("shs.server-actions.manage-subscription")}
                />
              )}
              {serverActions.includes("resume-subscription") && (
                <SelfHostedServerActionButton
                  onClick={handleStartSubscriptionClick}
                  glyph={"glyph-core-arrow-clockwise"}
                  text={i18nFormat("shs.server-actions.resume-subscription")}
                />
              )}
              {serverActions.includes("transfer-license") && (
                <SelfHostedServerActionButton
                  onClick={openTransferLicenseDialog}
                  glyph={"glyph-core-arrow-left-arrow-right"}
                  text={i18nFormat("shs.server-actions.transfer-license")}
                />
              )}
              {serverActions.includes("buy-lifetime-license") && (
                <SelfHostedServerActionButton
                  onClick={handleBuyLifetimeLicenseClick}
                  glyph={"glyph-core-cart-fill"}
                  text={i18nFormat("shs.server-actions.buy-lifetime-license")}
                />
              )}
              {breakpointDown("md") && (
                <React.Fragment>
                  <SelfHostedServerActionButton
                    onClick={openEditCustomDomainDialog}
                    glyph={"glyph-core-globe"}
                    text={i18nFormat("shs.context-menu.edit-custom-domain")}
                  />
                  <SelfHostedServerActionButton
                    onClick={openDeleteDialog}
                    glyph={"glyph-core-trash-fill"}
                    text={i18nFormat("shs.context-menu.delete-server")}
                    type={"delete"}
                  />
                </React.Fragment>
              )}
            </div>
          )}
        </div>
      </div>
      {serverActions.includes("transfer-license") && (
        <SelfHostedServerTransferLicenseDialog
          server={props.server}
          isOpen={isTransferLicenseDialogOpen}
          onClose={closeTransferLicenseDialog}
        />
      )}
      <SelfHostedServerDeleteDialog
        server={props.server}
        isOpen={isDeleteDialogOpen}
        onManageSubscription={handleManageSubscriptionClick}
        onClose={closeDeleteDialog}
      />
      <SelfHostedServerEditCustomDomainDialog
        server={props.server}
        isOpen={isEditCustomDomainDialogOpen}
        onClose={closeEditCustomDomainDialog}
      />
    </div>
  );
}

SelfHostedServer.propTypes = {
  server: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    selfHostedServerSubscriptionActive: PropTypes.bool.isRequired,
    selfHostedServerSubscriptionType: PropTypes.oneOf(["trial", "lifetime", "monthly"]).isRequired,
    selfHostedServerSubscriptionExpiresAt: PropTypes.string,
  }).isRequired,
};